import { Component, Inject, Input, LOCALE_ID, OnInit } from '@angular/core';
import { ModulePackagesService } from '@app/services/api/module-packages.service';
import { AccountModulePackage } from '@app/interfaces/packages/account-module-package';
import { ModulePackageUsage } from '@app/interfaces/packages/module-package-usage';
import { CurrentUsage } from '@app/interfaces/packages/current-usage';

@Component({
  selector: 'app-usage-dashboard',
  templateUrl: './usage-dashboard.component.html',
  standalone: false
})
export class UsageDashboardComponent implements OnInit {
  @Input() accountModulePackage!: AccountModulePackage;

  public currentUsage: CurrentUsage | null = null;
  public usageHistory: ModulePackageUsage[] = [];
  public loading = true;
  public showHistory = false;
  public loadError = false;

  constructor(
    private modulePackagesService: ModulePackagesService,
    @Inject(LOCALE_ID) private locale: string
  ) {}

  ngOnInit(): void {
    if (!this.accountModulePackage?.uuid) {
      console.error('UsageDashboardComponent: accountModulePackage is required');
      this.loading = false;
      this.loadError = true;
      return;
    }
    this.loadCurrentUsage();
  }

  loadCurrentUsage(): void {
    this.loading = true;
    this.loadError = false;

    this.modulePackagesService.getCurrentUsage(this.accountModulePackage.uuid)
      .subscribe({
        next: (response) => {
          this.currentUsage = response.data;
          this.loading = false;
        },
        error: (err) => {
          console.error('Failed to load current usage', err);
          this.loading = false;
          this.loadError = true;
        }
      });
  }

  loadUsageHistory(): void {
    if (!this.accountModulePackage?.uuid) return;

    this.modulePackagesService.getUsageHistory(this.accountModulePackage.uuid)
      .subscribe({
        next: (response) => {
          this.usageHistory = response.data;
          this.showHistory = true;
        },
        error: (err) => {
          console.error('Failed to load usage history', err);
        }
      });
  }

  toggleHistory(): void {
    if (!this.showHistory && this.usageHistory.length === 0) {
      this.loadUsageHistory();
    } else {
      this.showHistory = !this.showHistory;
    }
  }

  getTotalEstimated(): number {
    if (!this.currentUsage) return 0;
    const subscriptionFee = this.accountModulePackage?.subscriptionPrice ?? 0;
    const estimatedCharge = this.currentUsage.estimated_charge ?? 0;
    return subscriptionFee + estimatedCharge;
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(this.locale, { month: 'short', day: 'numeric' });
    } catch {
      return dateString;
    }
  }

  formatFullDate(dateString: string): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(this.locale, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateString;
    }
  }

  formatMonthYear(dateString: string): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(this.locale, { month: 'long', year: 'numeric' });
    } catch {
      return dateString;
    }
  }

  formatPrice(price: number | null | undefined): string {
    if (price == null) return '0.00';
    return price.toFixed(2);
  }
}
